import React, { useCallback, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import { Card, Modal, Tooltip } from '@components';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

interface Props {
  data: any; // Structure complète avec données, summary, period et aiAnalysis
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
}

export const SellerPerformance: React.FC<Props> = ({ data, onDateRangeChange }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
  const [activeMetric, setActiveMetric] = useState<'revenue' | 'quantity' | 'basket'>('revenue');

  // Gestion sécurisée des données
  const safeData = data?.data || [];
  const summary = data?.summary;
  const period = data?.period;
  const aiAnalysis = data?.aiAnalysis;

  const applyDateRange = useCallback(() => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);

    if (startDate && endDate) {
      onDateRangeChange(startDate, endDate);
    }
  }, [startDate, endDate, onDateRangeChange]);

  const resetDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    onDateRangeChange(null, null);
  };

  // Fonction pour formater les grands nombres
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return Math.round(num).toString();
  };

  const chartData = useMemo(() => {
    if (safeData.length === 0) return { labels: [], datasets: [] };

    const labels = safeData.map((item: any) => item.sellerName);
    let dataValues: number[] = [];
    let label = '';
    let backgroundColor = '';
    let borderColor = '';

    switch (activeMetric) {
      case 'revenue':
        dataValues = safeData.map((item: any) => item.totalRevenue);
        label = 'Revenus générés (FCFA)';
        backgroundColor = chartType === 'bar' 
          ? 'rgba(59, 130, 246, 0.6)' 
          : 'rgba(59, 130, 246, 0.1)';
        borderColor = 'rgba(59, 130, 246, 1)';
        break;
      case 'quantity':
        dataValues = safeData.map((item: any) => item.totalSoldQuantity);
        label = 'Quantités vendues';
        backgroundColor = chartType === 'bar' 
          ? 'rgba(34, 197, 94, 0.6)' 
          : 'rgba(34, 197, 94, 0.1)';
        borderColor = 'rgba(34, 197, 94, 1)';
        break;
      case 'basket':
        dataValues = safeData.map((item: any) => item.averageBasket || 0);
        label = 'Panier moyen (FCFA)';
        backgroundColor = chartType === 'bar' 
          ? 'rgba(168, 85, 247, 0.6)' 
          : 'rgba(168, 85, 247, 0.1)';
        borderColor = 'rgba(168, 85, 247, 1)';
        break;
    }

    return {
      labels,
      datasets: [
        {
          label,
          data: dataValues,
          backgroundColor,
          borderColor,
          borderWidth: 2,
          fill: chartType === 'line',
          tension: 0.4,
        },
      ],
    };
  }, [safeData, activeMetric, chartType]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            if (activeMetric === 'revenue' || activeMetric === 'basket') {
              return `${context.dataset.label}: ${formatNumber(value)} FCFA`;
            }
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: { 
        grid: { display: false },
        ticks: { 
          font: { size: 11 },
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 11 },
          callback: function(value: number) {
            if (activeMetric === 'revenue' || activeMetric === 'basket') {
              return formatNumber(value) + ' FCFA';
            }
            return value.toString();
          }
        },
        title: {
          display: true,
          text: activeMetric === 'revenue' ? 'Revenus (FCFA)' : 
                activeMetric === 'quantity' ? 'Quantités' : 'Panier moyen (FCFA)',
          font: { size: 12 },
        },
      },
    },
    elements: chartType === 'line' ? {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    } : {},
  }), [activeMetric, chartType]);

  return (
    <div className="space-y-6">
      {/* Métriques de résumé */}
      {summary && (
        <Card customClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Tooltip content="Nombre total de vendeurs actifs sur la période">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-blue-200 dark:border-blue-700">
                <div className="text-2xl mb-2">👥</div>
                <h4 className="font-semibold text-blue-800 dark:text-slate-100 mb-2">Vendeurs Actifs</h4>
                <p className="text-2xl font-bold text-blue-900 dark:text-gray-50">
                  {summary.totalSellers}
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Chiffre d'affaires total généré par tous les vendeurs">
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/30 dark:to-green-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-emerald-200 dark:border-emerald-700">
                <div className="text-2xl mb-2">💰</div>
                <h4 className="font-semibold text-emerald-800 dark:text-zinc-100 mb-2">CA Total</h4>
                <p className="text-xl font-bold text-emerald-900 dark:text-neutral-50">
                  {formatNumber(summary.totalRevenue)} FCFA
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Nombre total de ventes réalisées">
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/30 dark:to-violet-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-purple-200 dark:border-purple-700">
                <div className="text-2xl mb-2">🛒</div>
                <h4 className="font-semibold text-purple-800 dark:text-white mb-2">Ventes Totales</h4>
                <p className="text-2xl font-bold text-purple-900 dark:text-slate-50">
                  {summary.totalSales}
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Pourcentage de ventes avec paiements multiples">
              <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-amber-200 dark:border-amber-700">
                <div className="text-2xl mb-2">💳</div>
                <h4 className="font-semibold text-amber-800 dark:text-stone-100 mb-2">Paiements Multiples</h4>
                <p className="text-xl font-bold text-amber-900 dark:text-gray-100">
                  {summary.multiplePaymentPercentage?.toFixed(1) || 0}%
                </p>
              </div>
            </Tooltip>
          </div>
        </Card>
      )}

      {/* Graphique principal */}
      <Card customClassName="p-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Performance des Vendeurs
            {period && (
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                ({new Date(period.startDate).toLocaleDateString('fr-FR')} - {new Date(period.endDate).toLocaleDateString('fr-FR')})
              </span>
            )}
          </h3>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Sélecteur de métrique */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setActiveMetric('revenue')}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  activeMetric === 'revenue'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                💰 Revenus
              </button>
              <button
                onClick={() => setActiveMetric('quantity')}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  activeMetric === 'quantity'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                📦 Quantités
              </button>
              <button
                onClick={() => setActiveMetric('basket')}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  activeMetric === 'basket'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                🛒 Panier
              </button>
            </div>

            {/* Sélecteur de type de graphique */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  chartType === 'bar'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                📊 Barres
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  chartType === 'line'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                📈 Courbe
              </button>
            </div>

            {aiAnalysis && (
              <button
                onClick={() => setShowAnalysisModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <span className="text-lg">🤖</span>
                Analyse IA
              </button>
            )}
          </div>
        </div>

        {/* Sélecteurs de date */}
        <div className="flex flex-col sm:flex-row gap-4 items-end justify-end mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex flex-col sm:flex-row gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date de début
              </label>
              <input
                type="date"
                value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date de fin
              </label>
              <input
                type="date"
                value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={applyDateRange}
              disabled={!startDate || !endDate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm transition-colors"
            >
              Appliquer
            </button>
            {appliedStartDate && appliedEndDate && (
              <button 
                onClick={resetDateRange} 
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm transition-colors"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {safeData.length === 0 ? (
          <p className="text-center text-gray-500 italic py-8">
            Aucune donnée de performance pour la période sélectionnée.
          </p>
        ) : (
          <div className="relative h-64 md:h-80">
            {chartType === 'bar' ? (
              <Bar data={chartData} options={options as any} />
            ) : (
              <Line data={chartData} options={options as any} />
            )}
          </div>
        )}

        <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-400 text-center">
            📋 <strong>Insight:</strong> Comparez les performances de vos vendeurs pour identifier les tops performers et optimiser la formation de votre équipe.
          </p>
        </div>
      </Card>

      {/* Modal d'analyse IA */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title="🤖 Analyse IA - Performance des Vendeurs"
        customClassName="max-w-4xl"
      >
        {aiAnalysis && (
          <div className="space-y-6">
            {/* Badge de confiance */}
            <div className="flex justify-center">
              <span className="px-4 py-2 text-sm bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full border border-purple-200">
                <span className="font-semibold">Analyse disponible</span>
              </span>
            </div>

            {/* Message informatif */}
            <div className="p-6 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-800 dark:to-gray-800 rounded-xl shadow-sm border border-slate-300 dark:border-slate-600">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">📊</span>
                <h5 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
                  Analyse des Performances Commerciales
                </h5>
              </div>
              
              <div className="text-slate-700 dark:text-slate-200 leading-relaxed">
                <p className="mb-4">
                  L'analyse des performances de vos vendeurs révèle des insights précieux sur l'efficacité commerciale de votre équipe.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <h6 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Points d'analyse</h6>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                      <li>• Performance individuelle des vendeurs</li>
                      <li>• Analyse des paniers moyens</li>
                      <li>• Utilisation des méthodes de paiement</li>
                      <li>• Efficacité commerciale par période</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
                    <h6 className="font-semibold text-green-800 dark:text-green-200 mb-2">Optimisations possibles</h6>
                    <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                      <li>• Formation ciblée des équipes</li>
                      <li>• Motivation des top performers</li>
                      <li>• Amélioration des processus de vente</li>
                      <li>• Diversification des paiements acceptés</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations sur l'analyse */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Analyse disponible pour ce module
                </span>
                <span>
                  {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleDateString('fr-FR') : ''} à {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleTimeString('fr-FR') : ''}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
